import Autocomplete from "@mui/material/Autocomplete";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeviceActivityIcon } from "@/components/DeviceActivityIcon";
import { RESULTS_LIMIT_DEVICE_PICKER } from "@/constants/resultsLimits";
import {
  DEVICE_FIELDS_WITH_FIRMWARE,
  filterDevicesBySerial,
  useDevicesBySearch,
} from "@/hooks/devices/useDevicesBySearch";
import { useDeviceActivityStatus } from "@/hooks/statusInfos/useDeviceActivityStatus";
import { useDebounced } from "@/hooks/useDebounced";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";
import { deviceActivity } from "@/utilities/deviceActivity";

interface Props {
  value: DeviceWithVehicleInfo | null;
  onChange: (device: DeviceWithVehicleInfo | null) => void;
  /** Also offer devices without a serial number (trailers, equipment), e.g. for asset editing. */
  includeWithoutSerial?: boolean;
}

export function DeviceSelect({ value, onChange, includeWithoutSerial = false }: Props) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  // The firmware fields are asked for because the checklist reads that line straight off the
  // selected vehicle, and a short search applies this list as a property selector.
  const search = useDebounced(inputValue, 300);
  // Devices without a serial count towards the results limit too, so they are always fetched and
  // dropped here unless wanted: only the unfiltered count tells whether the cap was reached.
  const { data, isLoading, isPlaceholderData } = useDevicesBySearch(
    search,
    false,
    DEVICE_FIELDS_WITH_FIRMWARE,
    { includeWithoutSerial: true, limit: RESULTS_LIMIT_DEVICE_PICKER },
  );
  // A full page means more match. While a new search loads, the previous result stands in and
  // says nothing about the new one.
  const isCapped = !isPlaceholderData && (data?.length ?? 0) >= RESULTS_LIMIT_DEVICE_PICKER;
  const devices: DeviceWithVehicleInfo[] = useMemo(
    () => filterDevicesBySerial(data ?? [], includeWithoutSerial),
    [data, includeWithoutSerial],
  );

  const { statusInfoMap, isError: statusIsError } = useDeviceActivityStatus(devices, isOpen);

  return (
    <Autocomplete
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      options={devices}
      getOptionLabel={device => device.name ?? ""}
      getOptionKey={device => device.id}
      renderOption={(props, device) => (
        <li {...props} key={device.id}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: "100%" }}>
            <DeviceActivityIcon
              activity={deviceActivity(statusInfoMap[device.id], statusIsError)}
            />
            <Typography variant="body2" noWrap>
              {device.name}
              {device.licensePlate ? ` · ${device.licensePlate}` : ""}
            </Typography>
          </Stack>
        </li>
      )}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      loading={isLoading}
      sx={{ minWidth: 300 }}
      renderInput={params => (
        <TextField
          {...params}
          label={t("liveData.selectDevice", "Select device")}
          placeholder={t("liveData.searchDevice", "Search by name, license plate, or VIN")}
          size="small"
          helperText={
            isCapped ? t("liveData.searchCapped", { count: RESULTS_LIMIT_DEVICE_PICKER }) : undefined
          }
        />
      )}
    />
  );
}
